import React, { useEffect, useState, useRef } from 'react';
import {
	Box,
	BoxProps,
	Typography,
	Link,
	Button,
	Divider,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	CircularProgress,
	Checkbox,
	FormControlLabel,
	Dialog,
	DialogContent,
	IconButton as MuiIconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Seperator, TourChip, UserBlock } from '@app/shared/components';
import { PlyrVideo } from '@app/shared/components/PlyrVideo';
import { Tour } from '@app/shared/models/tour.type';
import { AccessibilityControls } from '@app/shared/components/accessibility/AccessibilityControls';
import { LinearWithValueLabel } from './Progress';
import { useTranslation } from 'react-i18next';
import { atom_tourState, atom_isDesktop } from '@app/shared/state/tour.atom';
import { mobilePlatform, isPwaInstalled } from '@app/shared/utils/env';
import { useRecoilState, useRecoilValue } from 'recoil';
import { getDeviceId } from '@app/shared/utils/device-id';
import { atom_downloadProgressBar } from '@app/shared/state/show-content-modal.atom';
import { atom_DownloadModeState } from '@app/shared/state/download-mode';
import { useRenderTracker } from '@app/shared/debug';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { TourDataFetcher } from '@app/features/tour-data-fetcher/tour-data-fetcher';
import startIllu from '@app/assets/images/start.png';
import midIllu from '@app/assets/images/mid.png';
import endIllu from '@app/assets/images/end.png';
import { deleteTourCache, removeTourFromDownloaded, markTourAsDownloaded, isTourDownloaded, isTourDownloadStale, getDownloadedTours, getDownloadedToursCount } from 'src/pages/delete-dbs';
import { useNavigate } from 'react-router-dom';
import { passiveCollector } from '@app/shared/services/passive-data/passive-data-collector';

type TourStartPageProps = {
	tour: Tour;
	onClickStart?: () => void;
	autoDownload?: boolean;
	children?: React.ReactNode;
} & BoxProps;

const wait = async (time: number) => {
	return new Promise((res: any, rej) => {
		setTimeout(() => {
			res();
		}, time);
	});
};

export const TourStartPage = React.forwardRef<BoxProps, TourStartPageProps>(
	function TourStartPage(_p, ref) {
		useRenderTracker('TourStartPage');
		const fetchTourOffline = TourDataFetcher();
		const navigate = useNavigate();

		const [isLoading, setIsLoading] = useState(false);
		const [isShowModal, setIsShowModal] = useState(false);
		const [isFinished, setIsFinished] = useState(false);
		const [hasReadConfirmation, setHasReadConfirmation] = useState(false);
		const { i18n, t } = useTranslation();
		const state_tourState = useRecoilValue(atom_tourState);
		const [uniqueId, setUniqueId] = useState('');
		const isDesktop = useRecoilValue(atom_isDesktop);
		const [state_progressBar, state_setProgressBar] = useRecoilState(
			atom_downloadProgressBar
		);
		const [_x, setState_activeStopState] = useRecoilState(
			atom_activeStopState
		);
		const [_y, setState_isDownloadMode] = useRecoilState(atom_DownloadModeState)

		const [downloading, setDownloading] = useState(false);
		const [downloadStep, setDownloadStep] = useState<'initial' | 'chooseTourToRemove' | 'step1' | 'done'>('initial');
		const [downloadedToursList, setDownloadedToursList] = useState<{ id: string; name: string; date: string }[]>([]);
		const [isDeletingCache, setIsDeletingCache] = useState(false);
		const [isTourDownloadedState, setIsTourDownloadedState] = useState(false);
		const [downloadStatusKey, setDownloadStatusKey] = useState<string>('');
		const [downloadStatusCount, setDownloadStatusCount] = useState<number | null>(null);
		const wakeLockRef = useRef<any>(null);
		const autoDownloadTriggeredRef = useRef(false);

		// Wake Lock functions to prevent screen from sleeping during download
		const requestWakeLock = async () => {
			try {
				if ('wakeLock' in navigator) {
					wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
				}
			} catch (err) {
				console.log('[WakeLock] Failed to acquire wake lock:', err);
			}
		};

		const releaseWakeLock = async () => {
			if (wakeLockRef.current) {
				try {
					await wakeLockRef.current.release();
					wakeLockRef.current = null;
				} catch (err) {
					console.log('[WakeLock] Failed to release wake lock:', err);
				}
			}
		};

		// HOME SCREEN / PWA INSTALL
		const [deferredInstallPrompt, setDeferredInstallPrompt] = useState<any>(null);
		const [canInstallPwa, setCanInstallPwa] = useState(false);
		const [showIosInstallGuide, setShowIosInstallGuide] = useState(false);

		// let uniqueDeviceId = "";

		// Record when tour start page is shown (for map time calculation)
		useEffect(() => { passiveCollector.trackTourStartPageOpened(); }, []);

		useEffect(() => {
			const mapLangToLangCode = {
				Hebrew: 'he',
				hebrew: 'he',
				English: 'en',
				english: 'en',
				Spanish: 'esp',
				spanish: 'esp',
				French: 'fr',
				french: 'fr',
				Portuguese: 'pt',
				portuguese: 'pt'
			}
			// Use shared device ID utility
			const uniqueID = getDeviceId();
			setUniqueId(uniqueID);
			const langCode = mapLangToLangCode[state_tourState?.language as keyof typeof mapLangToLangCode];
			console.log('[i18n] Tour language:', state_tourState?.language, '→ code:', langCode);
			(i18n as any).changeLanguage(langCode);
			document.dir = state_tourState?.language === 'Hebrew' ? 'rtl' : 'ltr';
		}, [state_tourState]);

		// Check if tour is downloaded on init
		useEffect(() => {
			const checkDownloadStatus = async () => {
				if (state_tourState?.id) {
					const isDownloaded = await isTourDownloaded(state_tourState.id);
					setIsTourDownloadedState(isDownloaded);
				}
			};
			checkDownloadStatus();
		}, [state_tourState?.id]);

		// Auto-open download dialog when navigated from MyToursPage with autoDownload flag
		useEffect(() => {
			if (_p.autoDownload && state_tourState?.id && !autoDownloadTriggeredRef.current) {
				autoDownloadTriggeredRef.current = true;
				handleDownloadClick();
			}
		}, [_p.autoDownload, state_tourState?.id]);

		// Capture PWA install prompt for "Add to Home Screen" button
		useEffect(() => {
			console.log('[PWA Debug] Setting up beforeinstallprompt listener');

			// Check if event was already captured globally before React mounted
			if ((window as any).deferredInstallPrompt) {
				console.log('[PWA Debug] Found globally captured install prompt');
				setDeferredInstallPrompt((window as any).deferredInstallPrompt);
				setCanInstallPwa(true);
			}

			const handler = (e: any) => {
				console.log('[PWA Debug] beforeinstallprompt event fired!', e);
				e.preventDefault();
				setDeferredInstallPrompt(e);
				setCanInstallPwa(true);
				// Also store globally in case component remounts
				(window as any).deferredInstallPrompt = e;
			};
			window.addEventListener('beforeinstallprompt', handler);

			// Check if already installed
			if (isPwaInstalled) {
				console.log('[PWA Debug] App is already installed (standalone mode)');
			}

			return () => window.removeEventListener('beforeinstallprompt', handler);
		}, []);

		const handleDownloadClick = () => {
			const currentTourId = state_tourState?.id;
			const count = getDownloadedToursCount();
			const isAlreadyDownloaded = currentTourId ? isTourDownloaded(currentTourId) : false;

			if (count >= 2 && !isAlreadyDownloaded) {
				// Show tour chooser
				const tours = getDownloadedTours();
				const list = Object.entries(tours).map(([id, info]) => ({
					id,
					name: info.name || id,
					date: new Date(info.timestamp).toLocaleDateString(),
				}));
				setDownloadedToursList(list);
				setDownloadStep('chooseTourToRemove');
			} else {
				setDownloadStep('initial');
			}
			setIsShowModal(true);
		};

		const renderChooseTourToRemove = () => {
			return <div>
				<Typography variant="h6">{t('pre_start_tour.on_download_modal.title_v2')}</Typography>
				<Divider sx={{ my: 1 }} />
				<Box sx={{ m: 2 }}>
					<Typography variant="body2" sx={{ mb: 2, wordBreak: 'break-word' }}>
						{t('download.max_tours_reached') || 'You have reached the maximum of 2 downloaded tours. Please choose one to remove:'}
					</Typography>
					{downloadedToursList.map((tour) => (
						<Box
							key={tour.id}
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								p: 1.5,
								mb: 1,
								borderRadius: 2,
								border: '1px solid #e0e0e0',
								cursor: 'pointer',
								'&:hover': { bgcolor: '#f5f5f5' },
							}}
							onClick={async () => {
								setIsDeletingCache(true);
								try {
									await deleteTourCache(tour.id);
									setDownloadStep('initial');
								} catch (error) {
									console.error('[Download] Error removing tour:', error);
								} finally {
									setIsDeletingCache(false);
								}
							}}
						>
							<Box>
								<Typography variant="body1" fontWeight={600}>{tour.name}</Typography>
								<Typography variant="caption" color="text.secondary">
									{t('download.downloaded_on') || 'Downloaded'}: {tour.date}
								</Typography>
							</Box>
							<Button
								variant="outlined"
								color="error"
								size="small"
								disabled={isDeletingCache}
							>
								{isDeletingCache ? <CircularProgress size={16} /> : (t('download.remove') || 'Remove')}
							</Button>
						</Box>
					))}
				</Box>
			</div>
		};

		const renderDownloadStep1 = () => {
			return <Box sx={{ borderRadius: '14px' }}>
				<Typography sx={{ fontWeight: 700, fontSize: '14px', lineHeight: '16px', letterSpacing: 0, verticalAlign: 'middle' }}>{t('pre_start_tour.on_download_modal.title_v2')}</Typography>
				<Divider sx={{ pt: '14px' }} />
				<Box sx={{ py: '14px' }}>
					<Typography style={{ wordBreak: 'break-word' }} variant='body2'>{t('pre_start_tour.on_download_modal.description_v2')}</Typography>
				</Box>
				<Box className="flex items-center m-5 justify-center">
					<img src={startIllu} />
				</Box>
				<Box
					className="flex items-center m-5 justify-center">
					<Button
						onClick={async () => {
							if (!state_tourState?.id) {
								console.error('[Download] No tour ID available');
								return;
							}

							setIsDeletingCache(true);
							try {
								// Clear cache for THIS tour only (not all tours)
								await deleteTourCache(state_tourState.id);
								setIsTourDownloadedState(false); // Update UI state

								// Advance to step 2 and auto-start download
								setDownloadStep('step1');
								// Request wake lock to prevent screen from sleeping during download
								await requestWakeLock();
								// Automatically start the download phase after a brief delay
								setTimeout(() => startDownloadPhase(), 500);
							} catch (error) {
								console.error('[Download] Error deleting cache:', error);
								// Release wake lock on error
								await releaseWakeLock();
							} finally {
								setIsDeletingCache(false);
							}
						}}
						disabled={isDeletingCache}
					>
						{isDeletingCache ? <CircularProgress size={24} /> : t('pre_start_tour.on_download_modal.button_v2')}
					</Button>

				</Box>
			</Box>
		}

		const renderDownloadStep3 = () => {
			return <div>
				<Typography >{t('pre_start_tour.on_download_modal.title_v2')}</Typography>
				<Divider />
				<Box className="flex items-center m-5">
					<Typography style={{ wordBreak: 'break-word' }} variant='body2'>{t('pre_start_tour.on_download_modal.description_v2_step3')}</Typography>
				</Box>

				<Box className="flex items-center m-5 justify-center"
				>
					<img src={midIllu} />
				</Box>
				{downloadStatusKey && (
					<Box className="flex items-center justify-center mb-2">
						<Typography variant='body2' style={{ fontWeight: 500, color: '#666' }}>
							{t(downloadStatusKey, downloadStatusCount ? { count: downloadStatusCount } : {})}
						</Typography>
					</Box>
				)}
				<Box className="">
					<LinearWithValueLabel isFinished={isFinished} />
				</Box>
				<Box
					className="flex items-center m-5 justify-center">

					<Button
						onClick={() => {
							setDownloadStep('done');
							setIsShowModal(false);
						}}
					>{
							t('pre_start_tour.on_download_modal.cancel')
						}</Button>
				</Box>
			</div>

		}

		const renderDownloadStep4 = () => {
			return <div>
				<Typography >{t('pre_start_tour.on_download_modal.title_v2')}</Typography>
				<Divider />
				<Box className="flex items-center m-5">
					<Typography style={{ wordBreak: 'break-word' }} variant='body2'>{t('pre_start_tour.on_download_modal.description_v2_step4')}</Typography>
				</Box>
				<Box className="flex items-center m-5 justify-center"
				>
					<img src={endIllu} />
				</Box>
				<Box className="flex flex-col gap-3 items-start m-5">
					<FormControlLabel
						control={
							<Checkbox
								checked={hasReadConfirmation}
								onChange={(e) => setHasReadConfirmation(e.target.checked)}
							/>
						}
						label={t('pre_start_tour.on_download_modal.read_confirmation')}
					/>
					<Button
						fullWidth
						variant="contained"
						disabled={!hasReadConfirmation}
						onClick={() => {
							setDownloadStep('done');
							setIsShowModal(false);
							setHasReadConfirmation(false);
						}}
					>
						{t('pre_start_tour.on_download_modal.close')}
					</Button>
				</Box>
			</div>
		}

		const startDownloadPhase = async () => {
			try {
				setDownloading(true);
				// Phase 1: Save tour content (0-80%)
				setDownloadStatusKey('pre_start_tour.on_download_modal.download_status_tour_content');
				// No need to wait for service worker - we're doing manual caching
				// This completes at 80% (handled in tour-data-fetcher.ts)
				const { sortedIndices, assets } = await fetchTourOffline(state_tourState as any);
				setState_isDownloadMode(true);

				// Phase 2: Save map for offline mode (80-98%)
				const stopsCount = state_tourState?.stops?.length || 0;
				if (stopsCount > 100) {
					setDownloadStatusKey('pre_start_tour.on_download_modal.download_status_map_many_stops');
					setDownloadStatusCount(stopsCount);
				} else {
					setDownloadStatusKey('pre_start_tour.on_download_modal.download_status_map');
					setDownloadStatusCount(null);
				}
				// Remaining 20%: Stop cycling and finalization (80% → 100%)
				// Use sortedIndices for optimized geographic order, fallback to sequential
				const stops = state_tourState?.stops || [];
				const indicesToUse = sortedIndices.length > 0 ? sortedIndices : stops.map((_, i) => i);
				const numOfStops = indicesToUse.length;
				const progressPerStop = 18 / numOfStops; // 18% total for all stops

				for (let i = 0; i < numOfStops; i++) {
					const stopIndex = indicesToUse[i];
					setState_activeStopState(stops[stopIndex]);
					await wait(2000);

					// Smooth increment: 80% + (progress per stop * stop number)
					const progress = Math.min(98, Math.round(80 + progressPerStop * (i + 1)));
					state_setProgressBar(progress);
				}

				// Phase 3: Last preparation (98-100%)
				setDownloadStatusKey('pre_start_tour.on_download_modal.download_status_last_prep');
				setState_activeStopState(state_tourState?.stops[0]);
				state_setProgressBar(99);
				await wait(1000);

				state_setProgressBar(100);
				await wait(500);

				// Mark tour as successfully downloaded
				if (state_tourState?.id) {
					markTourAsDownloaded(state_tourState.id, assets, state_tourState.title || state_tourState.id);
					setIsTourDownloadedState(true); // Update UI state
				}

				setDownloadStatusKey('');
				setIsFinished(true);
				passiveCollector.trackOfflineDownload();
			} catch (error) {
				console.error('[Download] Error during download phase:', error);
				setDownloadStatusKey('');
				await releaseWakeLock();
			} finally {
				setDownloading(false);
				// Release wake lock when download completes
				await releaseWakeLock();
			}
		};

		// const startDownloadPhase = async () => {
		// 	try {
		// 		setDownloading(true);
		// 		// No need to wait for service worker - we're doing manual caching
		// 		// This completes at 90% (handled in tour-data-fetcher.ts)
		// 		await fetchTourOffline(state_tourState as any);
		// 		setState_isDownloadMode(true);

		// 		// Cache routes for offline use (90% → 92%)
		// 		state_setProgressBar(90);
		// 		console.log('[Download] Starting route caching...');

		// 		// Prepare waypoints for route caching
		// 		const tourWaypoints = state_tourState?.stops
		// 			?.filter((stop) => stop.include_path !== false)
		// 			?.sort((a, b) => (a?.order || 0) - (b?.order || 0))
		// 			?.map((stop) => {
		// 				let directionType = stop?.direction_type || 'cycling-regular';
		// 				// Map direction types to API profile types
		// 				if (directionType === 'car') directionType = 'driving-car';
		// 				else if (directionType === 'none') directionType = 'none';
		// 				else if (directionType === 'foot') directionType = 'foot-walking';
		// 				else if (directionType === 'bike') directionType = 'cycling-regular';
		// 				else directionType = 'cycling-regular';

		// 				return {
		// 					type: directionType,
		// 					langAndLang: [
		// 						stop?.stop_location?.longitude || 0,
		// 						stop?.stop_location?.latitude || 0
		// 					] as [number, number]
		// 				};
		// 			}) || [];

		// 		if (tourWaypoints.length > 0) {
		// 			try {
		// 				const routeResult = await cacheRoutesForTour(tourWaypoints, (current: number, total: number) => {
		// 					// Progress from 90% to 92% as routes are cached
		// 					const routeProgress = 90 + Math.round((current / total) * 2);
		// 					state_setProgressBar(routeProgress);
		// 					console.log(`[Download] Caching routes: ${current}/${total} - Progress: ${routeProgress}%`);
		// 				});

		// 				console.log(`[Download] Route caching complete: ${routeResult.success} succeeded, ${routeResult.failed} failed`);
		// 				state_setProgressBar(92);
		// 			} catch (error) {
		// 				console.error('[Download] Error caching routes:', error);
		// 				// Continue even if route caching fails
		// 				state_setProgressBar(92);
		// 			}
		// 		} else {
		// 			console.log('[Download] No waypoints to cache routes for');
		// 			state_setProgressBar(92);
		// 		}

		// 		// Remaining 8%: Stop cycling and finalization (92% → 100%)
		// 		const numOfStops = state_tourState?.stops?.length || 0;
		// 		const progressPerStop = 6 / numOfStops; // 6% total for all stops (92% to 98%)

		// 		for (let i = 0; i < numOfStops; i++) {
		// 			setState_activeStopState(state_tourState?.stops[i]);
		// 			await wait(3000);

		// 			// Smooth increment: 92% + (progress per stop * stop number)
		// 			const progress = Math.min(98, Math.round(92 + progressPerStop * (i + 1)));
		// 			state_setProgressBar(progress);
		// 			console.log(`[Download] Stop ${i + 1}/${numOfStops} - Progress: ${progress}%`);
		// 		}

		// 		// Final steps: 98% → 100%
		// 		setState_activeStopState(state_tourState?.stops[0]);
		// 		state_setProgressBar(99);
		// 		await wait(1000);

		// 		state_setProgressBar(100);
		// 		await wait(500);

		// 		setIsFinished(true);
		// 	} catch (error) {
		// 		console.error('[Download] Error during download phase:', error);
		// 	} finally {
		// 		setDownloading(false);
		// 	}
		// };

		const renderDownloadStep2 = () => {
			return <div>
				<Typography >{t('pre_start_tour.on_download_modal.step2_title_v2')}</Typography>
				<Divider />
				<Box className="flex items-center m-5">
					<Typography style={{ wordBreak: 'break-word' }} variant='body1'>{t('pre_start_tour.on_download_modal.step2_description_v2')}</Typography>
				</Box>
				<Box className="flex items-center m-5 justify-center"
				>
					<img src={midIllu} />
				</Box>
				<Box className="flex items-center m-5 justify-center">
					<LinearWithValueLabel isFinished={isFinished} />
				</Box>


			</div>
		}

		const renderCurrentStep = () => {
			if (isFinished) return renderDownloadStep4();
			if (downloading) return renderDownloadStep3();
			if (downloadStep === 'chooseTourToRemove') return renderChooseTourToRemove();
			if (downloadStep === 'step1') return renderDownloadStep2();
			if (downloadStep === 'done') return <div></div>;
			return renderDownloadStep1();
		}

		return (
			<Box ref={ref} sx={{ position: 'relative', minHeight: '100vh', overflow: 'auto', maxWidth: 1200, margin: '0 auto' }}>
				{/* Hero media — rendered above the grid so content below starts cleanly after it */}
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'white', mb: 2 }}>
					{_p.tour?.starting_video && (
						<Box sx={{ width: { xs: '100%', md: '80%' }, maxWidth: 960 }}>
							<PlyrVideo
								url={_p.tour.starting_video.split('&token=')[0]}
								progressKey={_p.tour?.id ? `tour-start-${_p.tour.id}` : undefined}
								maxHeight="none"
							/>
						</Box>
					)}
					{_p.tour?.starting_image && (
						<Box sx={{
							width: { xs: '100%', md: '60%' },
							maxWidth: 820,
							display: 'flex',
							justifyContent: 'center',
							pt: '10px',
						}}>
							<img
								style={{
									maxWidth: '100%',
									maxHeight: '50vh',
									width: 'auto',
									height: 'auto',
									display: 'block',
									objectFit: 'contain',
									borderRadius: '5px',
								}}
								src={_p.tour?.starting_image.split('&token=')[0]}
							/>
						</Box>
					)}
				</Box>

				{/* Main content + sidebar layout for desktop */}
				<Box sx={{
					// margin: '0 auto',
					px: { xs: 0, md: 3 },
					display: { xs: 'block', md: 'flex' },
					gap: { md: 3 },
					pb: { xs: '180px', md: 0 }
				}}>
					{/* Left column: details */}
					<Box className="p-4" sx={{ minWidth: 0, flex: { md: '1 1 0%' } }}>
						<Typography variant="h1">{_p.tour?.title}</Typography>
						<Typography variant="body1" className="mb-4">{_p.tour?.main_sentense}</Typography>
						<Seperator />

						<UserBlock
							title={t('pre_start_tour.your_guide')}
							name={_p.tour?.tour_guide?.full_name}
							image_url={_p.tour?.tour_guide?.profile_image.split('&token')?.[0]}
						/>
						<Seperator />

						{(_p?.tour?.distance || _p?.tour?.duration || _p?.tour?.parking) && (
							<>
								<Typography variant="body2" className="mb-2" fontWeight={800}>
									{t('pre_start_tour.tour_details')}
								</Typography>
								<Box className="flex flex-wrap gap-4">
									{_p.tour?.distance ? (
										<TourChip icon_type="solid" icon_name="map-location" fontSize="0.8em">
											{(_p.tour?.distance / 1000).toFixed(1)} km
										</TourChip>
									) : <></>}
									{_p.tour?.duration ? (
										<TourChip icon_type="solid" icon_name="clock">
											{_p.tour?.duration}
										</TourChip>
									) : <></>}
								</Box>
								{_p?.tour?.parking ? (
									<div>
										<Seperator />
										<Box className="flex justify-between">
											<TourChip icon_type="solid" icon_name="p" fontSize="0.95em">
												<Link>{_p?.tour?.parking?.name}</Link>
											</TourChip>
										</Box>
									</div>
								) : <></>}
								<Seperator />
							</>
						)}

						{_p?.tour?.equipment?.length > 0 && (
							<>
								<Typography variant="body2" className="mb-2" fontWeight={800}>
									{t('pre_start_tour.important_to_bring')}
								</Typography>
								<Box className="flex flex-wrap gap-4 mb-4">
									{_p?.tour?.equipment?.map(item => (
										<TourChip
											key={'equipment' + item}
											icon_type="solid"
											icon_name={iconsMap[item] || iconsMap.default}
											fontSize="0.8em"
										>
											{item}
										</TourChip>
									))}
								</Box>
								<Seperator />
							</>
						)}

						{Array.isArray(_p.tour?.notes) && (
							<Box id="faqs-section" sx={{ pb: { xs: '200px', md: 0 } }}>
								{_p.tour?.notes.map((txt, i) => (
									<Accordion key={'noteskey' + i} className="mb-2">
										<AccordionSummary
											expandIcon={<span>&#9660;</span>}
											aria-controls={'panel-content-' + i}
											id={'panel-header-' + i}
										>
											<Typography variant="body1" style={{ fontWeight: 700 }}>
												{txt.question}
											</Typography>
										</AccordionSummary>
										<AccordionDetails>
											<Typography variant="body2" style={{ wordBreak: 'break-word' }}>
												{txt.answer}
											</Typography>
										</AccordionDetails>
									</Accordion>
								))}
							</Box>
						)}

						<Typography variant="body2" className="mb-4"></Typography>
					</Box>

					{/* Right column: sticky sidebar (desktop only) */}
					<Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 220, maxWidth: 300, flexShrink: 0 }}>
						<Box sx={{ position: 'sticky', top: 24 }}>
							<Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2, boxShadow: '0px 2px 12px 2px rgba(0,0,0,0.12)', mb: 2 }}>
								<Button fullWidth className="mb-3" onClick={() => { passiveCollector.trackTourStartPageClosed(); _p.onClickStart?.(); }}>
									{t('pre_start_tour.button_get_started')}
								</Button>
							</Box>
							<Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2, boxShadow: '0px 2px 12px 2px rgba(0,0,0,0.12)', mb: 2 }}>
								<Typography variant="body2" className="mb-2" fontWeight={400} style={{ wordBreak: 'break-word' }}>
									{t('pre_start_tour.download_description')}
								</Typography>
								<Button
									disabled={isDesktop || isTourDownloadedState}
									fullWidth
									onClick={handleDownloadClick}
								>
									{isTourDownloadedState
										? t('pre_start_tour.pre_download_modal.offline_mode_activated')
										: t('pre_start_tour.pre_download_modal.download')
									}
								</Button>
								{isTourDownloadedState && state_tourState?.id && isTourDownloadStale(state_tourState.id) && (
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
										<Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#EF4444', flexShrink: 0 }} />
										<Typography variant="caption" sx={{ color: '#EF4444', fontWeight: 500 }}>
											{t('my_tours.tour_card.download_stale')}
										</Typography>
									</Box>
								)}
							</Box>
							<Box sx={{ bgcolor: 'white', borderRadius: 2, p: 1, boxShadow: '0px 2px 12px 2px rgba(0,0,0,0.12)', mb: 1 }}>
								<AccessibilityControls position="top-right" />
							</Box>
						</Box>
					</Box>
				</Box>

				{/* Mobile sticky bottom buttons */}
				<Box sx={{
					display: { xs: 'block', md: 'none' },
					position: 'fixed',
					bottom: 0,
					left: 0,
					right: 0,
					bgcolor: 'white',
					borderTop: '1px solid #e0e0e0',
					p: 2,
					zIndex: 10000,
					boxShadow: '0px -2px 8px rgba(0,0,0,0.1)',
					pointerEvents: 'auto'
				}}>
					<Button
						fullWidth
						variant="contained"
						onClick={() => { passiveCollector.trackTourStartPageClosed(); _p.onClickStart?.(); }}
						sx={{ mb: 1 }}
					>
						{t('pre_start_tour.button_get_started')}
					</Button>
					<Button
						disabled={isDesktop /* || isTourDownloadedState */}
						fullWidth
						variant="outlined"
						onClick={handleDownloadClick}
						sx={{ mb: 1 }}
					>
						{t('pre_start_tour.pre_download_modal.download')}
						{/* {isTourDownloadedState
							? t('pre_start_tour.pre_download_modal.offline_mode_activated')
							: t('pre_start_tour.pre_download_modal.download')
						} */}
					</Button>
					{isTourDownloadedState && state_tourState?.id && isTourDownloadStale(state_tourState.id) && (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, px: 0.5 }}>
							<Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#EF4444', flexShrink: 0 }} />
							<Typography variant="caption" sx={{ color: '#EF4444', fontWeight: 500 }}>
								{t('my_tours.tour_card.download_stale')}
							</Typography>
						</Box>
					)}
					{/* HOME SCREEN / PWA INSTALL BUTTON - Android */}
					{canInstallPwa && mobilePlatform === 'android' && (
						<Button
							fullWidth
							variant="outlined"
							onClick={async () => {
								try {
									if (deferredInstallPrompt) {
										deferredInstallPrompt.prompt();
										const choiceResult = await deferredInstallPrompt.userChoice;
										if (choiceResult.outcome === 'accepted') {
											setCanInstallPwa(false);
											console.log('[PWA] Installed tour:', state_tourState?.id);
										}
									}
								} catch (e) {
									console.error('PWA install failed', e);
								}
							}}
							sx={{ mb: 1 }}
						>
							{t('Add to Home Screen')}
						</Button>
					)}
					{/* HOME SCREEN / PWA INSTALL BUTTON - iOS */}
					{mobilePlatform === 'ios' && !isPwaInstalled && (
						<Button
							fullWidth
							variant="outlined"
							onClick={() => setShowIosInstallGuide(true)}
							sx={{ mb: 1 }}
						>
							{t('Add to Home Screen')}
						</Button>
					)}
					<Button
						fullWidth
						variant="outlined"
						sx={{ mb: 1 }}
					>
						<AccessibilityControls position="bottom-right" />
					</Button>
				</Box>

				{/* Download modal */}
				<Dialog
					open={isShowModal}
					onClose={() => {
						setDownloadStep('done');
						setIsShowModal(false);
					}}
					dir={document.dir || 'ltr'}
					sx={{ zIndex: 999 }}
					PaperProps={{
						sx: {
							borderRadius: '16px',
							maxWidth: 420,
							width: '90%',
							m: 2,
						}
					}}
				>
					<MuiIconButton
						onClick={() => {
							setDownloadStep('done');
							setIsShowModal(false);
						}}
						sx={{
							position: 'absolute',
							top: 8,
							right: document.dir === 'rtl' ? 'auto' : 8,
							left: document.dir === 'rtl' ? 8 : 'auto',
							color: '#9CA3AF',
							zIndex: 1,
						}}
					>
						<CloseIcon fontSize="small" />
					</MuiIconButton>
					<DialogContent>
						{renderCurrentStep()}
					</DialogContent>
				</Dialog>

				{/* iOS Install Guide Modal */}
				{showIosInstallGuide && (
					<Box
						sx={{
							position: 'fixed',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							backgroundColor: 'rgba(0, 0, 0, 0.5)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							zIndex: 10001,
						}}
						onClick={() => setShowIosInstallGuide(false)}
					>
						<Box
							sx={{
								backgroundColor: '#fff',
								borderRadius: '16px',
								p: '24px',
								maxWidth: 340,
								width: '90%',
								textAlign: document.dir === 'rtl' ? 'right' : 'left',
								position: 'relative',
							}}
							onClick={(e) => e.stopPropagation()}
						>
							{/* Close button */}
							<Box
								onClick={() => setShowIosInstallGuide(false)}
								sx={{
									position: 'absolute',
									top: 12,
									right: document.dir === 'rtl' ? 'auto' : 12,
									left: document.dir === 'rtl' ? 12 : 'auto',
									cursor: 'pointer',
									color: '#9CA3AF',
									p: '4px',
									'&:hover': { color: '#111827' },
								}}
							>
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
									<line x1="18" y1="6" x2="6" y2="18"></line>
									<line x1="6" y1="6" x2="18" y2="18"></line>
								</svg>
							</Box>

							<Typography sx={{ fontSize: 18, fontWeight: 700, color: '#111827', mb: 2 }}>
								{t('my_tours.pwa.ios_title')}
							</Typography>

							{/* Step 1 */}
							<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2, flexDirection: document.dir === 'rtl' ? 'row-reverse' : 'row' }}>
								<Box
									sx={{
										width: 24,
										height: 24,
										borderRadius: '50%',
										backgroundColor: '#E65100',
										color: '#fff',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										fontSize: 13,
										fontWeight: 600,
										flexShrink: 0,
									}}
								>
									1
								</Box>
								<Box>
									<Typography sx={{ fontSize: 14, fontWeight: 600, color: '#111827', mb: 0.5 }}>
										{t('my_tours.pwa.ios_step1_title')}
									</Typography>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: document.dir === 'rtl' ? 'row-reverse' : 'row' }}>
										<Typography sx={{ fontSize: 13, color: '#4B5563' }}>
											{t('my_tours.pwa.ios_step1_description')}
										</Typography>
										<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E65100" strokeWidth="2">
											<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
											<polyline points="16 6 12 2 8 6"></polyline>
											<line x1="12" y1="2" x2="12" y2="15"></line>
										</svg>
									</Box>
								</Box>
							</Box>

							{/* Step 2 */}
							<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2, flexDirection: document.dir === 'rtl' ? 'row-reverse' : 'row' }}>
								<Box
									sx={{
										width: 24,
										height: 24,
										borderRadius: '50%',
										backgroundColor: '#E65100',
										color: '#fff',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										fontSize: 13,
										fontWeight: 600,
										flexShrink: 0,
									}}
								>
									2
								</Box>
								<Box>
									<Typography sx={{ fontSize: 14, fontWeight: 600, color: '#111827', mb: 0.5 }}>
										{t('my_tours.pwa.ios_step2_title')}
									</Typography>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: document.dir === 'rtl' ? 'row-reverse' : 'row' }}>
										<Typography sx={{ fontSize: 13, color: '#4B5563' }}>
											{t('my_tours.pwa.ios_step2_description')}
										</Typography>
										<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E65100" strokeWidth="2">
											<rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
											<line x1="12" y1="8" x2="12" y2="16"></line>
											<line x1="8" y1="12" x2="16" y2="12"></line>
										</svg>
									</Box>
								</Box>
							</Box>

							{/* Step 3 */}
							<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 3, flexDirection: document.dir === 'rtl' ? 'row-reverse' : 'row' }}>
								<Box
									sx={{
										width: 24,
										height: 24,
										borderRadius: '50%',
										backgroundColor: '#E65100',
										color: '#fff',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										fontSize: 13,
										fontWeight: 600,
										flexShrink: 0,
									}}
								>
									3
								</Box>
								<Box>
									<Typography sx={{ fontSize: 14, fontWeight: 600, color: '#111827', mb: 0.5 }}>
										{t('my_tours.pwa.ios_step3_title')}
									</Typography>
									<Typography sx={{ fontSize: 13, color: '#4B5563' }}>
										{t('my_tours.pwa.ios_step3_description')}
									</Typography>
								</Box>
							</Box>

							<Button
								fullWidth
								onClick={() => setShowIosInstallGuide(false)}
								sx={{
									backgroundColor: '#E65100',
									color: '#fff',
									py: 1.5,
									borderRadius: '12px',
									textTransform: 'none',
									fontSize: 15,
									fontWeight: 600,
									'&:hover': {
										backgroundColor: '#E65100',
										opacity: 0.9,
									},
								}}
							>
								{t('my_tours.pwa.got_it')}
							</Button>
						</Box>
					</Box>
				)}
			</Box>
		);
	}
);

const iconsMap: any = {
	מים: 'bottle-water mt-0.5',
	'סוללה מלאה': 'battery-bolt fa-rotate-270',
	'נעלי הליכה נוחות': 'shoe-prints',
	אוזניות: 'headphones',
	default: 'star'
};
